import { NATIVE_DEVICE_ID, settings } from "@/settings"

export class ApiError extends Error {
  constructor(
    public message: string,
    public data?: any
  ) {
    super(message)
    this.name = "ApiError"
  }
}

interface InternalMessage {
  id: string
  speaker: string
  text: string
}

interface Call {
  phone_number: string
  prompt: string
  id: number
  call_type: string
  voice_gender: string
  status: string
  scheduled_at: string | null
  summary: string
  succeeded: boolean
  missing_data: Array<{ name: string; description: string }>
  actions: Array<{ name: string; description: string }>
  dynamic_task: any
  contact?: Contact
  leave_voicemail_message?: string
  voicemail_message?: string
  // outbound_phone_number?: VonagePhoneNumber
  // outbound_phone_number_uuid?: string
  outbound_twilio_phone_number?: TwilioPhoneNumber
  outbound_twilio_phone_number_uuid?: string
  livekit_room_name?: string
  retries: number
  retry_delay_seconds: number
  retry_schedule: CallRetrySchedule
  handoff_phone_number_uuid?: string
  handoff_condition?: string
  voice_id?: string
  tts_type?: string
  task: any
  is_public: boolean
  public_uuid: string | null
  recording_url: string | null
  created_at: string
  duration: number
  transcript: any
  completed_at: string | null
  call_transferred: boolean
}

interface CallRetrySchedule {
  source_call: Call
  retries: number
  max_retries: number
  delay_between_retries_seconds: number
  completed: boolean
  succeeded: boolean
  call_retries: Call[]
}

interface Chat {
  id: number
  task_description: string | null
  theme: string
  created_at: string
  updated_at: string
  summary: string | null
}

interface ChatMessage {
  id: number
  content: string
  speaker: string
}

interface VoiceClone {
  id?: number
  country_code?: string | null
  phone_number?: string | null
  status?: string
  sample_audio_url?: string | null
  code?: string
}

interface ListVoiceClonesResults {
  results: VoiceClone[]
}

interface CallerSettingField {
  name: string
  description: string
  value: string
}

interface CallerSettings {
  voice_clone?: number | null
  standard_fields: CallerSettingField[]
  custom_fields: CallerSettingField[]
  voice_id?: string | null
  ai_model_slug?: string | null
}

interface TaskTemplateProperty {
  name: string
  description: string
  slug: string
  example: string
  default?: string | null // Optional, can be null
  required?: boolean | null // Optional, can be null
  html_type?: string | null // Optional, can be null
}

interface TaskTemplateCallbackRoute {
  uuid: string
  callback_phone_number: {
    uuid: string
    country_code: string
    phone_number: string
    formatted_number: string
    verified_at: string | null
  }
  forwarding_condition: string
  created_at: string
  updated_at: string
}

interface TaskTemplate {
  uuid: string // Read-only
  createdAt: string // Read-only, assuming ISO date format
  updatedAt: string // Read-only, assuming ISO date format
  taskName: string
  taskDescription: string
  taskInstructions: string
  taskProperties: TaskTemplateProperty[] | null // Array of TaskTemplateParameter
  fullTaskProperties?: TaskTemplateProperty[]
  recipientName?: string | null
  recipientPhoneNumber?: string | null
  recipientCountryCode?: string | null
  callLocale?: string | null
  iconoirIcon?: string | null
  internal?: boolean
  documentUploads?: DocumentUpload[]
  callback_routes?: TaskTemplateCallbackRoute[]
}

interface FeaturedTaskTemplateResponse {
  featuredTasks: TaskTemplate[]
  globalTags: string[]
}

interface CallbackPhoneNumber {
  uuid: string
  phone_number: string
  country_code: string
  verified_at: string | null
}

interface VonagePhoneNumber {
  phone_number: string
  country_code: string // Read-only
  msisdn: string // Read-only, assuming msisdn format
  uuid: string // Read-only
  callback_phone_number: CallbackPhoneNumber
  task_template: any
}

interface ContactCustomFieldMetadata {
  custom_field_keys: string[]
}

interface Contact {
  uuid: string
  custom_fields: Array<{ key: string; value: string }>
  custom_fields_map: { [key: string]: string }
  tags: string[]
  first_name?: string
  last_name?: string
  display_name?: string
  phone_number?: string
  country_code?: string
  merge_key?: string
  validation_errors?: any
}

function serializeTaskTemplate(taskTemplate: TaskTemplate): string {
  return JSON.stringify({
    task_name: taskTemplate.taskName,
    task_description: taskTemplate.taskDescription,
    task_instructions: taskTemplate.taskInstructions,
    task_properties: taskTemplate.taskProperties?.map((param) => ({
      name: param.name,
      description: param.description,
      slug: param.slug,
      example: param.example,
      default: param.default,
      required: param.required,
      html_type: param.html_type
    })),
    recipient_name: taskTemplate.recipientName,
    recipient_phone_number: taskTemplate.recipientPhoneNumber,
    recipient_country_code: taskTemplate.recipientCountryCode,
    call_locale: taskTemplate.callLocale,
    iconoir_icon: taskTemplate.iconoirIcon,
    internal: taskTemplate.internal
  })
}

function parseTaskTemplate(taskTemplate: any): TaskTemplate {
  const data = typeof taskTemplate === "string" ? JSON.parse(taskTemplate) : taskTemplate

  return {
    uuid: data.uuid,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
    taskName: data.task_name,
    taskDescription: data.task_description,
    taskInstructions: data.task_instructions,
    taskProperties: data.task_properties?.map((param: any) => ({
      name: param.name,
      description: param.description,
      slug: param.slug,
      example: param.example,
      default: param.default,
      required: param.required,
      html_type: param.html_type
    })),
    fullTaskProperties: data.full_task_properties?.map((param: any) => ({
      name: param.name,
      description: param.description,
      slug: param.slug,
      example: param.example,
      default: param.default,
      required: param.required,
      html_type: param.html_type
    })),
    recipientName: data.recipient_name,
    recipientPhoneNumber: data.recipient_phone_number,
    recipientCountryCode: data.recipient_country_code,
    callLocale: data.call_locale,
    iconoirIcon: data.iconoir_icon,
    internal: data.internal,
    documentUploads: data?.document_uploads?.map((upload: any) => ({
      id: upload.id,
      url: upload.url,
      name: upload.name
    })),
    callback_routes: data?.callback_routes?.map((route: any) => ({
      uuid: route.uuid,
      callback_phone_number: {
        uuid: route.callback_phone_number.uuid,
        country_code: route.callback_phone_number.country_code,
        phone_number: route.callback_phone_number.phone_number,
        formatted_number: route.callback_phone_number.formatted_number,
        verified_at: route.callback_phone_number.verified_at
      },
      forwarding_condition: route.forwarding_condition,
      created_at: route.created_at,
      updated_at: route.updated_at
    }))
  }
}

interface TaskTemplateParameterValue {
  slug: string
  value: string
}

interface TaskTemplateCall {
  taskTemplateUuid: string
  taskTemplateParameters?: TaskTemplateParameterValue[]
  recipientName?: string
  recipientPhoneNumber?: string
  recipientCountryCode?: string
  contactUuid?: string
}

function serializeTaskTemplateCall(taskTemplateCall: TaskTemplateCall): string {
  return JSON.stringify({
    task_template_uuid: taskTemplateCall.taskTemplateUuid,
    task_template_parameters: taskTemplateCall.taskTemplateParameters,
    recipient_name: taskTemplateCall.recipientName,
    recipient_phone_number: taskTemplateCall.recipientPhoneNumber,
    recipient_country_code: taskTemplateCall.recipientCountryCode,
    contact_uuid: taskTemplateCall.contactUuid
  })
}

interface ApiKey {
  uuid: string
  name: string
  key: string
}

function serializeApiKey(apiKey: ApiKey): string {
  return JSON.stringify({
    name: apiKey.name
  })
}

function parseApiKey(apiKey: any): ApiKey {
  return {
    uuid: apiKey.uuid,
    name: apiKey.name,
    key: apiKey.key
  }
}

interface Campaign {
  uuid?: string
  name?: string
  task_property_to_contact_field?: { [key: string]: string }
  task_template?: TaskTemplate
  timezone?: string
  daily_availability?: { [key: string]: string }[]
  paused?: boolean
  task_template_uuid?: string
  validation_error?: string
  queued_campaign_calls?: number
  completed_campaign_calls?: number
  invalid_campaign_calls?: number
  in_progress_campaign_calls?: number
}

function parseCampaign(campaign: any): Campaign {
  return {
    uuid: campaign.uuid,
    name: campaign.name,
    task_property_to_contact_field: campaign.task_property_to_contact_field,
    task_template: parseTaskTemplate(campaign.task_template),
    timezone: campaign.timezone,
    daily_availability: campaign.daily_availability,
    paused: campaign.paused,
    task_template_uuid: campaign.task_template?.uuid,
    validation_error: campaign.validation_error,
    queued_campaign_calls: campaign.queued_campaign_calls,
    completed_campaign_calls: campaign.completed_campaign_calls,
    invalid_campaign_calls: campaign.invalid_campaign_calls,
    in_progress_campaign_calls: campaign.in_progress_campaign_calls
  }
}

function serializeCampaign(campaign: Campaign): string {
  return JSON.stringify({
    name: campaign.name,
    task_property_to_contact_field: campaign.task_property_to_contact_field,
    task_template_uuid: campaign.task_template_uuid,
    timezone: campaign.timezone,
    paused: campaign.paused,
    daily_availability: campaign.daily_availability,
    validation_error: campaign.validation_error,
    queued_campaign_calls: campaign.queued_campaign_calls,
    completed_campaign_calls: campaign.completed_campaign_calls,
    invalid_campaign_calls: campaign.invalid_campaign_calls,
    in_progress_campaign_calls: campaign.in_progress_campaign_calls
  })
}

interface DocumentUpload {
  id: number
  url: string
  name: string
}

/**
 * Represents a task parameter used by a DynamicTask.
 */
export interface TaskParameter {
  name: string
  description: string
  slug: string
  html_type: string
  default_text?: string | null
  default_checked?: boolean | null
  options?: string[]
  required?: boolean | null
}

/**
 * Represents a DynamicTask resource.
 */
export interface DynamicTask {
  id: number
  uuid: string
  task: string | null
  task_short: string | null
  task_parameters: TaskParameter[]
  recipient_name: string
  phone_number: string
  task_description: string
  metadata: string | null
  create_url: string
  country_code: string
}

interface PrankCallCategory {
  slug: string
  name: string
}

interface PrankCallVoice {
  slug: string
  name: string
  description: string
  personality: string
  call_types: PrankCallType[]
  signature_call_types: PrankCallType[]
  demo_text?: string
  categories: PrankCallCategory[]
}

interface PrankCallType {
  slug: string
  name: string
  description: string
  prompt: string
  params: any[] // Could be expanded based on needs
}

interface BusinessSearchParams {
  query: string
  location?: string
  category?: string
  page?: number
  page_size?: number
}

interface BusinessSearchResult {
  id: number
  documentId: string
  name: string
  address: string | null
  phone: string | null
  countryCode: string | null
  shortDescription: string | null
  longDescription: string | null
  veryLongDescription: string | null
  location: string | null
  locationsServiced: string[] | null
  primaryCategory: string | null
  primaryCategorySlug: string | null
  categories: string[] | null
  categoriesSlugs: string[] | null
  slug: string | null
  bizPageBody: string | null
  commonIssues: string[] | null
  // commonIssuesGenerated: string[] | null
  websiteUrl: string | null
  city: string | null
  state: string | null
  citySlug: string | null
  createdAt: number | null
  updatedAt: number | null
  publishedAt: number | null
  externalId: string | null
  products: string[] | null
  advantages: string[] | null
  imageUrl: string | null
}

interface BusinessSearchResponse {
  results: BusinessSearchResult[]
  total: number
  page: number
  page_size: number
}

// Simple business search (DRF /api/search/business/) result shape
interface SimpleBusinessSearchResult {
  uuid: string
  name: string
  address: string | null
  phone: string | null
  country_code: string | null
  city: string | null
  state: string | null
}

interface SimpleBusinessSearchResponse {
  results: SimpleBusinessSearchResult[]
  total: number
  page: number
  page_size: number
}

// City search interfaces
interface CitySearchParams {
  query: string
  page?: number
  page_size?: number
}

interface CitySearchResult {
  uuid: string
  name: string
  state: string
  county: string
  slug: string
  population?: number | null
}

interface CitySearchResponse {
  results: CitySearchResult[]
  total: number
  page: number
  page_size: number
}

// Category search interfaces
interface CategorySearchParams {
  query: string
  page?: number
  page_size?: number
}

interface CategorySearchResult {
  uuid: string
  name: string
  slug: string
  description?: string
}

interface CategorySearchResponse {
  results: CategorySearchResult[]
  total: number
  page: number
  page_size: number
}

// Business Verification Requests
export interface BusinessVerificationRequestPayload {
  business_uuid: string
  verification_notes?: string
  supporting_documents?: string
  contact_full_name?: string
  contact_phone?: string
  contact_address_line1?: string
  contact_address_line2?: string
  contact_city?: string
  contact_state?: string
  contact_postal_code?: string
  contact_country?: string
}

export interface BusinessVerificationRequest {
  uuid: string
  business: {
    uuid: string
    name: string
    slug: string
    city: string | null
    state: string | null
  }
  status: string
  verification_code: string
  created_at: string
  reviewed_at?: string | null
  review_notes?: string
  verification_notes?: string
  supporting_documents?: string
  contact_full_name?: string
  contact_phone?: string
  contact_address_line1?: string
  contact_address_line2?: string
  contact_city?: string
  contact_state?: string
  contact_postal_code?: string
  contact_country?: string
}

export interface PaginatedBusinessVerificationRequests {
  results: BusinessVerificationRequest[]
  count: number
  next?: string | null
  previous?: string | null
}

interface CallTourScriptItem {
  display_text: string
  text: string
  voice_id: string
}

interface CallTourPlaylist {
  playlist: string[]
}

// Add TwilioPhoneNumber type alongside VonagePhoneNumber type
export interface TwilioPhoneNumber {
  uuid: string
  country_code: string
  phone_number: string
  callback_phone_number?: CallbackPhoneNumber
  task_template?: {
    uuid: string
    created_at: string
    updated_at: string
    task_name: string
    task_description: string
    task_instructions: string
    task_properties: any[]
    recipient_name: string | null
    recipient_phone_number: string | null
    recipient_country_code: string | null
    call_locale: string | null
    public: boolean
    featured: boolean
    tags: string[]
    iconoir_icon: string
    internal: boolean
    full_task_properties: any[]
    document_uploads: any[]
  }
}

// Add FrontendTask interface after other interfaces
interface FrontendTask {
  frontend_id: string
  title: string
  description: string
  category: string
  additional_metadata: any
  similarity?: number
  distance?: number
}

// Add this interface after the other interfaces
interface PushNotificationToken {
  token: string
  platform?: string
  device_name?: string | null
}

interface User {
  id: number
  uuid: string
  email: string
  vonage_user_id: string
  show_intro_flow: boolean
}

interface Plan {
  plan_slug: string
  tts_credits_per_minute: { [key: string]: number }
  model_credits_per_minute: { [key: string]: number }
  minutes_remaining: number | null
  calls_per_day_remaining: number | null
  minutes_per_day_remaining: number | null
  minutes_per_month_remaining: number | null
  total_calls: number
  subscription_updated_at: string | null
  needs_email_verification: boolean
  needs_phone_verification: boolean
}

// create VibratoClient class that has a token as a property
// and methods to make API calls

export class VibratoClient {
  token: string
  expiresIn?: number
  expiresAt?: number
  getToken?: () => Promise<any>
  errorCallback?: (error: any) => Promise<void>
  maxRetries: number = 4

  constructor(
    token: string,
    expiresIn?: number,
    getToken?: () => Promise<any>,
    errorCallback?: (error: any) => Promise<void>
  ) {
    this.token = token
    this.expiresIn = expiresIn
    this.getToken = getToken
    this.expiresAt = expiresIn ? Date.now() + expiresIn * 1000 : undefined
    this.errorCallback = errorCallback

    console.log("VibratoClient: expiresIn", this.expiresIn, "expiresAt", this.expiresAt)
  }

  private async refreshToken(): Promise<void> {
    const shouldRefresh = this.getToken && this.expiresAt && Date.now() > this.expiresAt
    if (!shouldRefresh) {
      return
    }

    console.log("VibratoClient: token expired, refreshing")

    let retries = 0
    let lastError = null

    while (retries < this.maxRetries) {
      try {
        const token = await this.getToken!()
        this.token = token.access_token
        this.expiresIn = token.expires_in
        this.expiresAt = Date.now() + token.expires_in * 1000

        console.log("VibratoClient: token refreshed, expiresIn", this.expiresIn, "expiresAt", this.expiresAt)
        return
      } catch (error) {
        lastError = error
        console.warn(`VibratoClient: failed to refresh token (attempt ${retries + 1}/${this.maxRetries})`, error)
        retries++

        if (retries < this.maxRetries) {
          const backoffTime = 1000 * Math.pow(2, retries)
          await new Promise((resolve) => setTimeout(resolve, backoffTime))
        }
      }
    }

    if (lastError) {
      console.error("VibratoClient: failed to refresh token", lastError)
    }
    if (this.errorCallback && lastError) {
      await this.errorCallback(lastError)
    }

    throw lastError
  }

  private async fetchWithCredentials(url: string, options: RequestInit = {}): Promise<Response> {
    await this.refreshToken()

    // Get platform and device info for native environments
    const platformHeaders = await this.getPlatformHeaders()

    const defaultOptions: RequestInit = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this.token}`,
        ...platformHeaders
      },
      credentials: "include"
    }

    // Merge the default options with the provided options
    const mergedOptions: RequestInit = {
      ...defaultOptions,
      ...options,
      headers: {
        ...defaultOptions.headers,
        ...(options.headers || {})
      }
    }

    return fetch(url, mergedOptions)
  }

  private async getPlatformHeaders(): Promise<Record<string, string>> {
    const headers: Record<string, string> = {}

    if (settings.IS_NATIVE_APP) {
      headers["X-Vibrato-Platform"] = settings.PLATFORM
      if (NATIVE_DEVICE_ID) {
        headers["X-Vibrato-Device-Id"] = NATIVE_DEVICE_ID
      }
    }

    return headers
  }

  async getLivekitToken(roomName: string) {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/users/livekit_token?room=${roomName}`)

    if (!res.ok) {
      throw new Error("Unable to get Livekit token")
    }

    const data = await res.json()
    return data.token
  }

  async resendEmailVerification() {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/users/resend_email_verification/`, {
      method: "POST"
    })

    if (!res.ok) {
      throw new Error("Unable to resend email verification")
    }

    const data = await res.json()
    return data
  }

  async getCall(callId: number): Promise<Call> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/calls/${callId}/`)

    if (!res.ok) {
      const data = await res.json()
      throw new Error(data.detail)
    }

    const data = await res.json()
    return data
  }

  async getCalls(options?: {
    status?: string
    since?: string | null
    pageLink?: string
    pageSize?: number
    page?: number
    campaignUuid?: string
    contactUuid?: string
  }) {
    let url = `${settings.VIBRATO_URL}/api/calls/?`

    if (options?.pageLink) {
      url = options.pageLink
    }

    const params: { [key: string]: any } = {}

    if (options?.status) {
      params["status"] = options.status
    }
    if (options?.since) {
      params["since"] = options.since
    }
    if (options?.pageSize) {
      params["page_size"] = options.pageSize
    }
    if (options?.page) {
      params["page"] = options.page
    }
    if (options?.campaignUuid) {
      params["campaign_uuid"] = options.campaignUuid
    }
    if (options?.contactUuid) {
      params["contact_uuid"] = options.contactUuid
    }

    const paramsStr = new URLSearchParams(params).toString()
    url += paramsStr

    const res = await this.fetchWithCredentials(url)

    if (!res.ok) {
      const data = await res.json()
      throw new Error(data.detail)
    }

    const data = await res.json()
    return data
  }

  async createCall(params: any): Promise<Call> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/calls/`, {
      method: "POST",
      body: JSON.stringify({
        ...params
      })
    })

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while creating the call.", data)
    }

    const data = await res.json()
    return data
  }

  async createCallFromDynamicTask(uuid: string): Promise<Call> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/calls/create_from_dynamic_task/`, {
      method: "POST",
      body: JSON.stringify({
        uuid: uuid
      })
    })

    if (!res.ok) {
      const data = await res.json()
      throw new Error(data.detail)
    }

    const data = await res.json()
    return data
  }

  async shareCall(callId: number): Promise<Call> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/calls/${callId}/share/`, {
      method: "POST"
    })

    if (!res.ok) {
      const data = await res.json()
      throw new Error(data.detail)
    }

    const data = await res.json()
    return data
  }

  async unshareCall(callId: number): Promise<Call> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/calls/${callId}/unshare/`, {
      method: "POST"
    })

    if (!res.ok) {
      const data = await res.json()
      throw new Error(data.detail)
    }

    const data = await res.json()
    return data
  }

  async deleteTaskTemplateDocument(taskTemplateUuid: string, documentId: number): Promise<void> {
    const res = await this.fetchWithCredentials(
      `${settings.VIBRATO_URL}/api/task_templates/${taskTemplateUuid}/remove_document/`,
      {
        method: "DELETE",
        body: JSON.stringify({
          document_id: documentId
        })
      }
    )
    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while deleting task template document.", data)
    }
  }

  async createCallFromTaskTemplate(taskTemplateCall: TaskTemplateCall): Promise<Call> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/calls/create_from_task_template/`, {
      method: "POST",
      body: serializeTaskTemplateCall(taskTemplateCall)
    })

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while creating call from task template.", data)
    }

    const data = await res.json()
    return data
  }

  async updateCall(id: number, params: any) {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/calls/${id}/`, {
      method: "PATCH",
      body: JSON.stringify(params)
    })

    if (!res.ok) {
      throw new Error(await res.text())
    }

    const data = await res.json()
    return data
  }

  async createCallInstructions(callId: number, instructions: string): Promise<Call> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/calls/${callId}/instructions/`, {
      method: "POST",
      body: JSON.stringify({
        instructions: instructions
      })
    })

    if (!res.ok) {
      const data = await res.json()
      throw new Error(data.detail)
    }

    const data = await res.json()
    return data
  }

  async takeOverCall(callId: number, countryCode: string, phoneNumber: string): Promise<Call> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/calls/${callId}/take_over_phone_call/`, {
      method: "POST",
      body: JSON.stringify({
        country_code: countryCode,
        phone_number: phoneNumber
      })
    })

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while taking over phone call.", data)
    }

    const data = await res.json()
    return data
  }

  async execCall(callId: number): Promise<Call> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/calls/${callId}/execute/`, {
      method: "POST"
    })

    if (!res.ok) {
      const data = await res.json()
      throw new Error(data.detail)
    }

    const data = await res.json()
    return data
  }

  async createRetryCall(callId: number): Promise<Call> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/calls/${callId}/create_retry_call/`, {
      method: "POST"
    })

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while creating retry call.", data)
    }

    const data = await res.json()
    return data
  }

  async endCall(callId: number): Promise<Call> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/calls/${callId}/end/`, {
      method: "POST"
    })

    if (!res.ok) {
      const data = await res.json()
      throw new Error(data.detail)
    }

    const data = await res.json()
    return data
  }

  async getCallRecording(callId: number): Promise<any> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/calls/${callId}/recording/`)

    if (!res.ok) {
      const data = await res.json()
      throw new Error(data.detail)
    }

    const data = await res.json()
    return data.recording_url
  }

  async getDocumentUrls(callId: number): Promise<any> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/calls/${callId}/documents/`)

    if (!res.ok) {
      const data = await res.json()
      throw new Error(data.detail)
    }

    const data = await res.json()
    return data.documents
  }

  async getCallTasks(): Promise<any> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/calls/tasks`)

    if (!res.ok) {
      const data = await res.json()
      throw new Error(data.detail)
    }

    const data = await res.json()
    return data
  }

  async getUserApiSession(): Promise<any> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/users/me/`)

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while getting user API session.", data)
    }

    const data = await res.json()
    return data
  }

  async getUserAccountVerification(): Promise<any> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/users/account_verification/`, {
      method: "POST"
    })

    if (!res.ok) {
      const data = await res.json()
      throw new Error(data.detail)
    }

    const data = await res.json()
    return data
  }

  async getUserStripeDetails(): Promise<any> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/users/stripe_details/`)

    if (!res.ok) {
      const data = await res.json()
      throw new Error(data.detail)
    }

    const data = await res.json()
    return data
  }

  async switchStripePlan(plan: string): Promise<any> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/users/switch_stripe_plan/`, {
      method: "POST",
      body: JSON.stringify({
        plan: plan
      })
    })

    if (!res.ok) {
      const data = await res.json()
      throw new Error(data.detail)
    }

    const data = await res.json()
    return data
  }

  async getUserSubscriptionCheckoutDetails(): Promise<any> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/users/stripe_checkout_details/`)

    if (!res.ok) {
      const data = await res.json()
      throw new Error(data.detail)
    }

    const data = await res.json()
    return data
  }

  async getSubscription(): Promise<any> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/users/subscription/`)

    if (!res.ok) {
      const data = await res.json()
      throw new Error(data.detail)
    }

    const data = await res.json()
    return data
  }

  async getAppleSubscription(): Promise<any> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/users/apple_subscription/`)

    if (!res.ok) {
      const data = await res.json()
      throw new Error(data.detail)
    }

    const data = await res.json()
    return data
  }

  async registerAppleSubscription(params: {
    originalTransactionId: string
    transactionId: string
    productId: string
  }): Promise<{ status: string }> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/apple_subscriptions/`, {
      method: "POST",
      body: JSON.stringify({
        original_transaction_id: params.originalTransactionId,
        transaction_id: params.transactionId,
        product_id: params.productId
      })
    })

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while registering Apple subscription.", data)
    }

    const data = await res.json()
    return data
  }

  async callWebsocket(callId: number) {
    await this.refreshToken()

    const encodedToken = encodeURIComponent(this.token)

    const ws = new WebSocket(`${settings.VIBRATO_WS_URL}/api/ws/calls/${callId}?token=${encodedToken}`)

    return ws
  }

  async getActions(status?: string) {
    const statusParam = status ? status : "active"

    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/actions/?status=${statusParam}`)

    if (!res.ok) {
      const data = await res.json()
      throw new Error(data.detail)
    }

    const data = await res.json()

    return data
  }

  async updateAction(id: number, params: any) {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/actions/${id}/`, {
      method: "PATCH",
      body: JSON.stringify(params)
    })

    if (!res.ok) {
      const data = await res.json()
      throw new Error(data.detail)
    }

    const data = await res.json()

    return data
  }

  async getTasks() {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/tasks/`)

    if (!res.ok) {
      const data = await res.json()
      throw new Error(data.detail)
    }

    const data = await res.json()

    return data
  }

  async getRecipientType(slug: string) {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/recipient_types/${slug}/`)

    if (!res.ok) {
      const data = await res.json()
      throw new Error(data.detail)
    }

    const data = await res.json()

    return data
  }

  async createChat(theme: string, taskDescription?: string) {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/chats/`, {
      method: "POST",
      body: JSON.stringify({
        theme: theme,
        task_description: taskDescription
      })
    })

    if (!res.ok) {
      const data = await res.json()
      throw new Error(data.detail)
    }

    const data = await res.json()

    return data
  }

  async getChat(chatId: number): Promise<Chat> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/chats/${chatId}/`)

    if (!res.ok) {
      const data = await res.json()
      throw new Error(data.detail)
    }

    const data = await res.json()

    return data
  }

  async getChatTranscript(chatId: number): Promise<ChatMessage[]> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/chats/${chatId}/transcript/`)

    if (!res.ok) {
      const data = await res.json()
      throw new Error(data.detail)
    }

    const data = await res.json()
    return data
  }

  async getChats(options?: { pageSize?: number; page?: number }): Promise<{ results: Chat[]; count: number }> {
    const params = new URLSearchParams()
    if (options?.pageSize) params.append("page_size", options.pageSize.toString())
    if (options?.page) params.append("page", options.page.toString())

    const url = `${settings.VIBRATO_URL}/api/chats/?${params.toString()}`
    const res = await this.fetchWithCredentials(url)

    if (!res.ok) {
      const data = await res.json()
      throw new Error(data.detail)
    }

    const data = await res.json()
    return data
  }

  async createChatHumanMessage(chatId: number, message: string): Promise<ChatMessage> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/chats/${chatId}/human_message/`, {
      method: "POST",
      body: JSON.stringify({
        content: message
      })
    })

    if (!res.ok) {
      const data = await res.json()
      throw new Error(data.detail)
    }

    const data = await res.json()

    return data
  }

  async *createChatResponse(chatId: number) {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/chats/${chatId}/generate_response/`, {
      method: "POST"
    })

    if (!res.ok) {
      const errorData = await res.json()
      throw new Error(errorData.detail)
    }

    if (!res.body) {
      return
    }

    // Assuming the response is a ReadableStream
    const reader = res.body.getReader()
    let { value: chunk, done: readerDone } = await reader.read()
    let lines = ""

    while (!readerDone) {
      const decodedChunk = new TextDecoder().decode(chunk)
      lines += decodedChunk

      let lineEndIndex
      while ((lineEndIndex = lines.indexOf("\n")) >= 0) {
        const line = lines.substring(0, lineEndIndex).trim()
        lines = lines.substring(lineEndIndex + 1)

        if (line) {
          yield JSON.parse(line)
        }
      }

      ;({ value: chunk, done: readerDone } = await reader.read())
    }

    // Process any remaining line after the loop has finished.
    if (lines.trim()) {
      yield JSON.parse(lines)
    }
  }

  async createDemoCall(name: string, countryCode: string, phone_number: string, locale: string): Promise<any> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/demo_calls/`, {
      method: "POST",
      body: JSON.stringify({
        recipient_name: name,
        phone_number: phone_number,
        country_code: countryCode,
        locale: locale
      })
    })

    if (!res.ok) {
      const data = await res.text()
      throw new Error(data)
    }

    const data = await res.json()
    return data
  }

  async createCallFeedback(callId: number, feedback: string | null, stars: number | null): Promise<any> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/calls/${callId}/feedback/`, {
      method: "POST",
      body: JSON.stringify({
        feedback: feedback,
        stars: stars
      })
    })

    if (!res.ok) {
      const data = await res.json()
      throw new Error(data.detail)
    }

    const data = await res.json()
    return data
  }

  async getCallerSettings(): Promise<CallerSettings> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/caller_settings/`)

    if (!res.ok) {
      const data = await res.json()
      throw new Error(data.detail)
    }

    const data = await res.json()
    return data
  }

  async updateCallerSettings(callerSettings: CallerSettings): Promise<CallerSettings> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/caller_settings/`, {
      method: "PATCH",
      body: JSON.stringify(callerSettings)
    })

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("Error saving caller settings.", data)
    }

    const data = await res.json()
    return data
  }

  async getVoiceClones(): Promise<VoiceClone[]> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/voice_clones/`)

    if (!res.ok) {
      const data = await res.json()
      throw new Error(data.detail)
    }

    const data = await res.json()
    return data
  }

  async createVoiceClone(voiceClone: VoiceClone): Promise<VoiceClone> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/voice_clones/`, {
      method: "POST",
      body: JSON.stringify(voiceClone)
    })

    if (!res.ok) {
      const data = await res.json()
      throw new Error(data.detail)
    }

    const data = await res.json()
    return data
  }

  async getVoiceClone(voiceCloneId: number): Promise<VoiceClone> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/voice_clones/${voiceCloneId}/`)

    if (!res.ok) {
      const data = await res.json()
      throw new Error(data.detail)
    }

    const data = await res.json()
    return data
  }

  async listVoiceClones(): Promise<ListVoiceClonesResults> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/voice_clones/?active=1`)

    if (!res.ok) {
      const data = await res.json()
      throw new Error(data.detail)
    }

    const data = await res.json()
    return data
  }

  async updateVoiceClone(voiceCloneId: number, voiceClone: VoiceClone): Promise<VoiceClone> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/voice_clones/${voiceCloneId}/`, {
      method: "PATCH",
      body: JSON.stringify(voiceClone)
    })

    if (!res.ok) {
      const data = await res.json()
      throw new Error(data.detail)
    }

    const data = await res.json()
    return data
  }

  async executeVoiceClone(voiceCloneId: number): Promise<VoiceClone> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/voice_clones/${voiceCloneId}/execute/`, {
      method: "POST"
    })

    if (!res.ok) {
      const data = await res.json()
      throw new Error(data.detail)
    }

    const data = await res.json()
    return data
  }

  async getPlan(): Promise<Plan> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/users/plan/`)

    if (!res.ok) {
      const data = await res.json()
      throw new Error(data.detail)
    }

    const data = await res.json()

    return data
  }

  async getTaskTemplates(): Promise<TaskTemplate[]> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/task_templates/`)

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while fetching task templates.", data)
    }

    const data = await res.json()

    return data.results.map((template: any) => parseTaskTemplate(template))
  }

  async getFeaturedTaskTemplates(): Promise<FeaturedTaskTemplateResponse> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/task_templates/featured/`)

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while fetching featured task templates.", data)
    }

    const data = await res.json()

    return {
      featuredTasks: data.featured_tasks.map((template: any) => parseTaskTemplate(template)),
      globalTags: data.global_tags
    }
  }

  async searchPublicTaskTemplates(query: string): Promise<TaskTemplate[]> {
    const encodedQuery = encodeURIComponent(query)
    const res = await this.fetchWithCredentials(
      `${settings.VIBRATO_URL}/api/task_templates/search_public/?q=${encodedQuery}`
    )

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while searching public task templates.", data)
    }

    const data = await res.json()

    return data.map((template: any) => parseTaskTemplate(template))
  }

  async getTaskTemplate(uuid: string): Promise<TaskTemplate> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/task_templates/${uuid}/`)

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while fetching task template.", data)
    }

    const data = await res.json()

    return parseTaskTemplate(data)
  }

  async createTaskTemplate(taskTemplate: TaskTemplate): Promise<TaskTemplate> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/task_templates/`, {
      method: "POST",
      body: serializeTaskTemplate(taskTemplate)
    })

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while creating task template.", data)
    }

    const data = await res.json()

    return parseTaskTemplate(data)
  }

  async createTaskTemplateFromCall(callId: number): Promise<TaskTemplate> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/task_templates/create_from_call/`, {
      method: "POST",
      body: JSON.stringify({
        call_id: callId
      })
    })

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while creating task template from call.", data)
    }

    const data = await res.json()

    return parseTaskTemplate(data)
  }

  async updateTaskTemplate(uuid: string, taskTemplate: TaskTemplate): Promise<TaskTemplate> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/task_templates/${uuid}/`, {
      method: "PATCH",
      body: serializeTaskTemplate(taskTemplate)
    })

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while updating task template.", data)
    }

    const data = await res.json()

    return parseTaskTemplate(data)
  }

  async deleteTaskTemplate(uuid: string): Promise<void> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/task_templates/${uuid}/`, {
      method: "DELETE"
    })

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while deleting task template.", data)
    }
  }

  async createTaskTemplateRoute(
    taskTemplateUuid: string,
    callbackPhoneNumberUuid: string,
    forwardingCondition: string
  ): Promise<TaskTemplateCallbackRoute> {
    const res = await this.fetchWithCredentials(
      `${settings.VIBRATO_URL}/api/task_templates/${taskTemplateUuid}/routes/`,
      {
        method: "POST",
        body: JSON.stringify({
          callback_phone_number_uuid: callbackPhoneNumberUuid,
          forwarding_condition: forwardingCondition
        })
      }
    )

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while creating task template route.", data)
    }

    const data = await res.json()
    return data
  }

  async updateTaskTemplateRoute(
    taskTemplateUuid: string,
    routeUuid: string,
    callbackPhoneNumberUuid: string,
    forwardingCondition: string
  ): Promise<TaskTemplateCallbackRoute> {
    const res = await this.fetchWithCredentials(
      `${settings.VIBRATO_URL}/api/task_templates/${taskTemplateUuid}/routes/${routeUuid}/`,
      {
        method: "PATCH",
        body: JSON.stringify({
          callback_phone_number_uuid: callbackPhoneNumberUuid,
          forwarding_condition: forwardingCondition
        })
      }
    )

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while updating task template route.", data)
    }

    const data = await res.json()
    return data
  }

  async deleteTaskTemplateRoute(taskTemplateUuid: string, routeUuid: string): Promise<void> {
    const res = await this.fetchWithCredentials(
      `${settings.VIBRATO_URL}/api/task_templates/${taskTemplateUuid}/routes/${routeUuid}/`,
      {
        method: "DELETE"
      }
    )

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while deleting task template route.", data)
    }
  }

  async getContacts(options?: {
    campaign_uuid?: string
    pageLink?: string
    pageSize?: number
    page?: number
    showInvalid?: boolean
  }): Promise<Contact[]> {
    let url = `${settings.VIBRATO_URL}/api/contacts/?`

    if (options?.pageLink) {
      url = options.pageLink
    }

    const params: { [key: string]: any } = {}

    if (options?.campaign_uuid) {
      params["campaign_uuid"] = options.campaign_uuid
    }
    if (options?.pageSize) {
      params["page_size"] = options.pageSize
    }
    if (options?.page) {
      params["page"] = options.page
    }
    if (options?.showInvalid) {
      params["invalid"] = "true"
    }

    const paramsStr = new URLSearchParams(params).toString()
    url += paramsStr

    const res = await this.fetchWithCredentials(url)

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while fetching contacts.", data)
    }

    const data = await res.json()

    return data
  }

  async validateContacts(contacts: Contact[]): Promise<Contact[]> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/contacts/validate/`, {
      method: "POST",
      body: JSON.stringify({
        contacts: contacts
      })
    })

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while validating contacts.", data)
    }

    const data = await res.json()

    return data
  }

  async getContact(uuid: string): Promise<Contact> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/contacts/${uuid}/`)

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while fetching contact.", data)
    }

    const data = await res.json()

    return data
  }

  async updateContact(contact: Contact): Promise<Contact> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/contacts/${contact.uuid}/`, {
      method: "PATCH",
      body: JSON.stringify(contact)
    })

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while updating contact.", data)
    }

    const data = await res.json()

    return data
  }

  async createContact(contact: Contact): Promise<Contact> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/contacts/`, {
      method: "POST",
      body: JSON.stringify(contact)
    })

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while creating contact.", data)
    }

    const data = await res.json()

    return data
  }

  async deleteContact(uuid: string): Promise<void> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/contacts/${uuid}/`, {
      method: "DELETE"
    })

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while deleting contact.", data)
    }
  }

  async batchCreateContacts(contacts: Contact[]): Promise<Contact[]> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/contacts/batch_create/`, {
      method: "POST",
      body: JSON.stringify({
        contacts: contacts
      })
    })

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while batch creating contacts.", data)
    }

    const data = await res.json()

    return data
  }

  async getContactCustomFieldsMetadata(): Promise<ContactCustomFieldMetadata> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/contacts/custom_fields_metadata/`)

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while fetching contact custom fields metadata.", data)
    }

    const data = await res.json()

    return data
  }

  async createApiKey(apiKey: ApiKey): Promise<ApiKey> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/api_keys/`, {
      method: "POST",
      body: serializeApiKey(apiKey)
    })

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while creating API key.", data)
    }

    const data = await res.json()

    return parseApiKey(data)
  }

  async getApiKeys(): Promise<ApiKey[]> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/api_keys/`)

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while fetching API keys.", data)
    }

    const data = await res.json()

    return data.results.map((key: any) => parseApiKey(key))
  }

  async deleteApiKey(uuid: string): Promise<void> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/api_keys/${uuid}/`, {
      method: "DELETE"
    })

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while deleting API key.", data)
    }
  }

  async getCampaigns(): Promise<Campaign[]> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/campaigns/`)

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while fetching campaigns.", data)
    }

    const data = await res.json()

    return data.results.map((campaign: any) => parseCampaign(campaign))
  }

  async getCampaign(uuid: string): Promise<Campaign> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/campaigns/${uuid}/`)

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while fetching campaign.", data)
    }

    const data = await res.json()

    return parseCampaign(data)
  }

  async createCampaign(campaign: Campaign): Promise<Campaign> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/campaigns/`, {
      method: "POST",
      body: serializeCampaign(campaign)
    })

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while creating campaign.", data)
    }

    const data = await res.json()

    return parseCampaign(data)
  }

  async pauseCampaign(uuid: string): Promise<Campaign> {
    return await this.updateCampaign(uuid, { paused: true })
  }

  async unpauseCampaign(uuid: string): Promise<Campaign> {
    return await this.updateCampaign(uuid, { paused: false })
  }

  async updateCampaign(uuid: string, campaign: Campaign): Promise<Campaign> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/campaigns/${uuid}/`, {
      method: "PATCH",
      body: serializeCampaign(campaign)
    })

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while updating campaign.", data)
    }

    const data = await res.json()

    return parseCampaign(data)
  }

  async deleteCampaign(uuid: string): Promise<void> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/campaigns/${uuid}/`, {
      method: "DELETE"
    })

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while deleting campaign.", data)
    }
  }

  async getCampaignContacts(uuid: string): Promise<Contact[]> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/campaigns/${uuid}/contacts/`)

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while fetching campaign contacts.", data)
    }

    const data = await res.json()

    return data.results
  }

  async addContactToCampaign(uuid: string, contactUuid: string): Promise<void> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/campaigns/${uuid}/contacts/`, {
      method: "POST",
      body: JSON.stringify({
        contact_uuid: contactUuid
      })
    })

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while adding contact to campaign.", data)
    }

    const data = await res.json()
    return data
  }

  async batchAddContactsToCampaign(uuid: string, contactUuids: string[], includeProcessed: boolean): Promise<void> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/campaigns/${uuid}/contacts/batch/`, {
      method: "POST",
      body: JSON.stringify({
        contact_uuids: contactUuids,
        include_processed: includeProcessed
      })
    })

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while batch adding contacts to campaign.", data)
    }
  }

  async removeContactFromCampaign(uuid: string, contactUuids: string[]): Promise<void> {
    const csvUuids = contactUuids.join(",")
    const res = await this.fetchWithCredentials(
      `${settings.VIBRATO_URL}/api/campaigns/${uuid}/delete_contacts/?contact_uuids=${csvUuids}`,
      {
        method: "DELETE"
      }
    )

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while removing contacts from campaign.", data)
    }
  }

  async createCallbackPhoneNumberConfirmation(countryCode: string, phoneNumber: string): Promise<any> {
    const res = await this.fetchWithCredentials(
      `${settings.VIBRATO_URL}/api/callback_phone_numbers/create_confirmation/`,
      {
        method: "POST",
        body: JSON.stringify({
          country_code: countryCode,
          phone_number: phoneNumber
        })
      }
    )

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while creating callback phone number confirmation.", data)
    }

    const data = await res.json()

    return data
  }

  async deleteCallbackPhoneNumber(uuid: string): Promise<void> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/callback_phone_numbers/${uuid}/`, {
      method: "DELETE"
    })

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while deleting callback phone number.", data)
    }
  }

  async confirmCallbackPhoneNumber(uuid: string, code: string): Promise<void> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/callback_phone_numbers/${uuid}/confirm/`, {
      method: "POST",
      body: JSON.stringify({ code })
    })

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while confirming callback phone number.", data)
    }

    const data = await res.json()

    return data
  }

  async getCallbackPhoneNumbers(): Promise<CallbackPhoneNumber[]> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/callback_phone_numbers/?verified=true`)

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while fetching phone numbers.", data)
    }

    const data = await res.json()

    return data.results
  }

  async getDynamicTasks(options?: { pageLink?: string; pageSize?: number; page?: number }): Promise<DynamicTask[]> {
    let url = `${settings.VIBRATO_URL}/api/dynamic_tasks/?`

    if (options?.pageLink) {
      url = options.pageLink
    }

    const params: { [key: string]: any } = {}
    if (options?.pageSize) {
      params["page_size"] = options.pageSize
    }
    if (options?.page) {
      params["page"] = options.page
    }

    const paramsStr = new URLSearchParams(params).toString()
    url += paramsStr

    const res = await this.fetchWithCredentials(url)

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while fetching dynamic tasks.", data)
    }

    const data = await res.json()
    // Assuming the API returns a paginated response with a "results" array:
    return data.results
  }

  async getDynamicTask(uuid: string): Promise<DynamicTask> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/dynamic_tasks/${uuid}/`)

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while fetching the dynamic task.", data)
    }

    const data = await res.json()
    return data
  }

  async createDynamicTask(task: DynamicTask): Promise<DynamicTask> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/dynamic_tasks/`, {
      method: "POST",
      body: JSON.stringify(task)
    })

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while creating dynamic task.", data)
    }

    const data = await res.json()

    return data
  }

  async updateDynamicTask(id: number, task: DynamicTask | any): Promise<DynamicTask> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/dynamic_tasks/${id}/`, {
      method: "PATCH",
      body: JSON.stringify(task)
    })

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while updating dynamic task.", data)
    }

    const data = await res.json()
    return data
  }

  async deleteDynamicTask(id: number): Promise<void> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/dynamic_tasks/${id}/`, {
      method: "DELETE"
    })

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while deleting dynamic task.", data)
    }
  }

  async getPrankCallVoices(): Promise<PrankCallVoice[]> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/prank_calls/voices/`)

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("Failed to fetch prank call voices", data)
    }

    const data = await res.json()
    return data.voices
  }

  async getVoiceDemo(voiceSlug: string): Promise<{ audioUrl: string }> {
    const res = await this.fetchWithCredentials(
      `${settings.VIBRATO_URL}/api/prank_calls/voice_demo/?voice_slug=${voiceSlug}`
    )

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("Failed to fetch voice demo", data)
    }

    const data = await res.json()
    return { audioUrl: data.audio_url }
  }

  async createPrankCall(params: {
    voiceSlug: string
    callTypeSlug: string
    recipientPhoneNumber: string
    recipientCountryCode: string
  }): Promise<Call> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/prank_calls/`, {
      method: "POST",
      body: JSON.stringify({
        voice_slug: params.voiceSlug,
        call_type_slug: params.callTypeSlug,
        recipient_phone_number: params.recipientPhoneNumber,
        recipient_country_code: params.recipientCountryCode
      })
    })

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("Failed to create prank call", data)
    }

    const data = await res.json()
    return data
  }

  async searchBusinesses(params: BusinessSearchParams): Promise<BusinessSearchResponse> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/business-search/`, {
      method: "POST",
      body: JSON.stringify(params)
    })

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while searching businesses.", data)
    }

    const data = await res.json()
    return data
  }

  // Uses DRF SearchViewSet (/api/search/business/) backed by Business.search (Typesense)
  async searchBusinessesSimple(params: BusinessSearchParams): Promise<SimpleBusinessSearchResponse> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/search/business/`, {
      method: "POST",
      body: JSON.stringify(params)
    })

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while searching businesses.", data)
    }

    const data = await res.json()
    return data
  }

  // City search using DRF SearchViewSet (/api/search/city/)
  async searchCities(params: CitySearchParams): Promise<CitySearchResponse> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/search/city/`, {
      method: "POST",
      body: JSON.stringify(params)
    })

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while searching cities.", data)
    }

    const data = await res.json()
    return data
  }

  // Category search using DRF SearchViewSet (/api/search/category/)
  async searchCategories(params: CategorySearchParams): Promise<CategorySearchResponse> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/search/category/`, {
      method: "POST",
      body: JSON.stringify(params)
    })

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while searching categories.", data)
    }

    const data = await res.json()
    return data
  }

  // Business Verification Requests API
  async createBusinessVerificationRequest(
    payload: BusinessVerificationRequestPayload
  ): Promise<BusinessVerificationRequest> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/business_verification_requests/`, {
      method: "POST",
      body: JSON.stringify(payload)
    })
    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("Failed to submit business verification request.", data)
    }
    return await res.json()
  }

  async getBusinessVerificationRequest(uuid: string): Promise<BusinessVerificationRequest> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/business_verification_requests/${uuid}/`)
    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("Failed to fetch business verification request.", data)
    }
    return await res.json()
  }

  async listBusinessVerificationRequests(options?: {
    page?: number
    pageSize?: number
  }): Promise<PaginatedBusinessVerificationRequests> {
    const params = new URLSearchParams()
    if (options?.page) params.append("page", String(options.page))
    if (options?.pageSize) params.append("page_size", String(options.pageSize))
    const url = `${settings.VIBRATO_URL}/api/business_verification_requests/?${params.toString()}`
    const res = await this.fetchWithCredentials(url)
    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("Failed to list business verification requests.", data)
    }
    return await res.json()
  }

  async deleteBusinessVerificationRequest(uuid: string): Promise<void> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/business_verification_requests/${uuid}/`, {
      method: "DELETE"
    })
    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("Failed to delete business verification request.", data)
    }
  }

  // Verified Businesses API
  async getVerifiedBusinesses(): Promise<any[]> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/verified_businesses/`)
    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("Failed to fetch verified businesses.", data)
    }
    const data = await res.json()
    return data.results || data
  }

  async getVerifiedBusiness(uuid: string): Promise<any> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/verified_businesses/${uuid}/`)
    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("Failed to fetch verified business.", data)
    }
    return await res.json()
  }

  async updateVerifiedBusiness(uuid: string, payload: any): Promise<any> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/verified_businesses/${uuid}/`, {
      method: "PATCH",
      body: JSON.stringify(payload)
    })
    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("Failed to update verified business.", data)
    }
    return await res.json()
  }

  async getTwilioPhoneNumbers(): Promise<TwilioPhoneNumber[]> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/twilio_phone_numbers/`)

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while fetching Twilio phone numbers.", data)
    }

    const data = await res.json()

    return data.results
  }

  async getTwilioPhoneNumber(uuid: string): Promise<TwilioPhoneNumber> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/twilio_phone_numbers/${uuid}/`)

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while fetching Twilio phone number.", data)
    }

    const data = await res.json()

    return data
  }

  async getAvailableTwilioPhoneNumbers(
    countryCode: string,
    areaCode?: string,
    beginsWithDigits?: string
  ): Promise<any[]> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/twilio_phone_numbers/available/`, {
      method: "POST",
      body: JSON.stringify({
        country_code: countryCode,
        area_code: areaCode,
        begins_with: beginsWithDigits
      })
    })

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while fetching available Twilio phone numbers.", data)
    }

    const data = await res.json()

    return data
  }

  async createTwilioPhoneNumber(phoneNumber: string): Promise<TwilioPhoneNumber> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/twilio_phone_numbers/`, {
      method: "POST",
      body: JSON.stringify({
        phone_number: phoneNumber
      })
    })

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while creating Twilio phone number.", data)
    }

    const data = await res.json()

    return data
  }

  async updateTwilioPhoneNumber(
    uuid: string,
    callbackNumberUuid?: string,
    taskTemplateUuid?: string
  ): Promise<TwilioPhoneNumber> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/twilio_phone_numbers/${uuid}/`, {
      method: "PATCH",
      body: JSON.stringify({
        callback_phone_number_uuid: callbackNumberUuid,
        task_template_uuid: taskTemplateUuid
      })
    })

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while updating Twilio phone number.", data)
    }

    const data = await res.json()

    return data
  }

  async deleteTwilioPhoneNumber(uuid: string): Promise<void> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/twilio_phone_numbers/${uuid}/`, {
      method: "DELETE"
    })

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while deleting Twilio phone number.", data)
    }
  }

  async searchFrontendTasks(query: string, limit: number = 10, threshold: number = 0.9): Promise<FrontendTask[]> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/frontend_tasks/search/`, {
      method: "POST",
      body: JSON.stringify({
        query,
        limit,
        threshold
      })
    })

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while searching frontend tasks.", data)
    }

    const data = await res.json()
    return data
  }

  async registerPushNotificationToken(token: PushNotificationToken): Promise<PushNotificationToken> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/push_notification_tokens/`, {
      method: "POST",
      body: JSON.stringify({
        token: token.token,
        platform: token.platform || "ios",
        device_name: token.device_name
      })
    })

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while registering push notification token.", data)
    }

    const data = await res.json()
    return data
  }

  async requestAccountDeletion(reason: string, comments: string): Promise<{ success: boolean; message: string }> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/users/request_account_deletion/`, {
      method: "POST",
      body: JSON.stringify({
        reason,
        comments
      })
    })

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while requesting account deletion.", data)
    }

    const data = await res.json()
    return data
  }

  async completeIntroFlow(): Promise<{ success: boolean }> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/users/complete_intro_flow/`, {
      method: "POST"
    })

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while completing the intro flow.", data)
    }

    const data = await res.json()
    return data
  }

  async registerReferral(
    referralUuid: string
  ): Promise<{ success: boolean; message: string; referring_user_email?: string }> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/users/register_referral/`, {
      method: "POST",
      body: JSON.stringify({
        referral_uuid: referralUuid
      })
    })

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while registering referral.", data)
    }

    const data = await res.json()
    return data
  }

  async registerGiftSubscription(
    giftUuid: string
  ): Promise<{ success: boolean; message: string; gifting_user_email?: string; trial_days?: number }> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/users/register_gift_subscription/`, {
      method: "POST",
      body: JSON.stringify({
        gift_uuid: giftUuid
      })
    })

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while registering gift subscription.", data)
    }

    const data = await res.json()
    return data
  }

  async createCallTourPlaylist(script: CallTourScriptItem[], hmac: string): Promise<CallTourPlaylist> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/call_tours/`, {
      method: "POST",
      body: JSON.stringify({
        script: script,
        hmac: hmac
      })
    })

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while creating call tour playlist.", data)
    }

    const data = await res.json()
    return data
  }

  async applyPromoCode(promoCode: string): Promise<{ success: boolean; message: string }> {
    const res = await this.fetchWithCredentials(`${settings.VIBRATO_URL}/api/users/apply_promo_code/`, {
      method: "POST",
      body: JSON.stringify({
        promo_code: promoCode
      })
    })

    if (!res.ok) {
      const data = await res.json()
      throw new ApiError("An error occurred while applying promo code.", data)
    }

    const data = await res.json()
    return data
  }
}

export type {
  ApiKey,
  BusinessSearchParams,
  BusinessSearchResponse,
  BusinessSearchResult,
  Call,
  CallbackPhoneNumber,
  CallerSettingField,
  CallerSettings,
  CallTourPlaylist,
  CallTourScriptItem,
  CategorySearchParams,
  CategorySearchResult,
  CategorySearchResponse,
  Chat,
  ChatMessage,
  CitySearchParams,
  CitySearchResult,
  CitySearchResponse,
  Contact,
  FrontendTask,
  InternalMessage,
  PrankCallCategory,
  PrankCallType,
  PrankCallVoice,
  PushNotificationToken,
  TaskTemplate,
  TaskTemplateCall,
  TaskTemplateCallbackRoute,
  TaskTemplateParameterValue,
  TaskTemplateProperty,
  VoiceClone,
  VonagePhoneNumber,
  User,
  Plan
}
